import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { i18nIntl, isNotEmpty } from '../../lib/ep.js';

import { CreateButton, MainButton, SquaredButton } from '../builder/ActionButton';
import FloatingLayer from '../builder/FloatingLayer';
import SectionTitle from '../builder/SectionTitle';

import TriviaBulkCreate from './TriviaBulkCreate.js';
import AIGenerator from './AIGenerator.js';
import AIPalorderGenerator from './AIPalorderGenerator.js';
import AICrosswordsGenerator from './AICrosswordsGenerator.js';
import Tooltip from '../builder/Tooltip.js';
import IconedSelectorItems from '../wrappers/IconedSelectorItems.js';
import SectionSubtitle from '../builder/SectionSubtitle.js';
import ButtonCollection from '../builder/ButtonCollection.js';
import { AlertMessage } from '../builder/Message.js';

export const PlanAlertIA = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const type_selector = props?.type_selector ?? false;
	if(!type_selector) return null;

	return (
		<FloatingLayer
				backButton={() => props.close()}
			>	
				<div>
					<SectionTitle autotranslate={false}>{type_selector.title_ai}</SectionTitle>

					<SectionSubtitle autotranslate={false}>
						{props.intl.formatMessage({
							id:"Unlock the power of AI with our state-of-the-art {single} Generator. Create a wide range of topic-based {plural} seamlessly to enhance your {type}."
						}, {
							single:type_selector.single,
							plural:type_selector.plural,
							type:type_selector.type,
						})}
					</SectionSubtitle>

					<SectionSubtitle autotranslate={false}>
						{props.intl.formatMessage({
							id:"Our innovative tool uses advanced algorithms to generate engaging and relevant {plural} effortlessly."
						}, {
							plural:type_selector.plural,
						})}
					</SectionSubtitle>

					<SectionSubtitle autotranslate={false}>
						{props.intl.formatMessage({
							id:"To access this powerful feature, simply subscribe to our platform and start exploring the limitless possibilities of AI-generated {plural}."
						}, {
							plural:type_selector.plural,
						})}
					</SectionSubtitle>

					<ButtonCollection in_floating={true}>
						<MainButton
							_href={`${window.panel_url}plans/add/premium?ai`}
							_target='_blank'
						>{props.intl.formatMessage({ id:"Purchase {type}" }, { type:i18n("Premium Plan") })}</MainButton>
					</ButtonCollection>
				</div>
			</FloatingLayer>
	)
}

const MultiOptionsCreator = ({ onCreateSimple, onCreateBulk, onCreateAI, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ display_create_layer, setDisplayCreateLayer ] = useState(false);
	const [ create_type, setCreateType ] = useState(false);

	const promotion = props?.promotion ?? {};
	const promotion_id = promotion?.id ?? false;

	if(promotion_id === false) return null;

	const valid_types = {
		palorder:{
			label_ai:"Create topic-based words with our AI word generator.",
			title_ai:i18n("AI Word Generator"),
			single:i18n("word",'ucfirst'),
			plural:i18n("words"),
			type:i18n("Word Shuffle"),
		},
		crosswords:{
			label_ai:"Create topic-based words with our AI word generator.",
			title_ai:i18n("AI Word Generator"),
			single:i18n("word",'ucfirst'),
			plural:i18n("words"),
			type:i18n("Crossword"),
		},
		crosswords_definition:{
			label_ai:"Create a definition based on a Crossword word with our AI word generator.",
			title_ai:i18n("AI Definition Generator"),
			single:i18n("definition",'ucfirst'),
			plural:i18n("definition"),
			type:i18n("Crossword"),
		},
		timed_quiz:{
			label_ai:"Create topic-based questions with our AI question generator.",
			title_ai:i18n("AI Question Generator"),
			single:i18n("Question",'lowercase'),
			plural:i18n("Questions",'lowercase'),
			type:i18n("Quiz"),
		},
	};

	const type_selector = (props.type_ai != undefined) ? valid_types[props.type_ai] : false;
	if(type_selector == undefined || !type_selector) return null;

	const closeLayers = () => {
		setDisplayCreateLayer(false);
		setCreateType(false);
	};

	const create_types = [{
		id:'simple',
		name:"Simple",
		desc:i18n("Simple form to quickly add a question and its options."),
		skip:props.skip_simple,
		cta_label:"Continue",
	}, {
		id:'bulk',
		name:i18n("In bulk"),
		desc:i18n("You can import multiple questions from a CSV or Excel file."),
		skip:props.skip_bulk,
		cta_label:"Continue",
	}, {
		id:'ai',
		name:i18n("With Artificial Intelligence"),
		desc:i18n(type_selector.label_ai),
		skip:props.skip_ai,
		cta_label:"Continue",
		icon:'knk-wizard',
		styles:{
			backgroundColor:'rgba(var(--ep-yellow-color), 0.7)',
		},
	}];

	const selectAddingItemsOption = (type) => {
		switch(type.id) {
			case 'simple':
				if(onCreateSimple != undefined) {
					closeLayers();
					onCreateSimple();
					return;
				}
	
				setCreateType(type.id);
				return;

			case 'bulk':
				if(onCreateBulk != undefined) {
					closeLayers();
					onCreateBulk();
					return;
				}
	
				setCreateType(type.id);
				return;

			case 'ai':
				if(onCreateAI != undefined) {
					closeLayers();
					onCreateAI();
					return;
				}

				setCreateType(props?.type_ai ?? type.id);
				return;

			default:
				setCreateType(false);
				break;
		}
	};

	const label_create = props?.label_button ?? "Create new questions";
	const label_main = props?.label_main ?? "Add questions";

	const squared_button = props?.squared_button ?? false;

	return (
		<>
			{(squared_button) && (
				<SquaredButton
					icon='knk-layers'
					tooltip={<Tooltip
						with_icon={false}
						position='bottom'
					>{label_create}</Tooltip>}
					onClick={() => setDisplayCreateLayer(true)}
					aria-label="Create"
				/>
			)}

			{(!squared_button) && (
				<CreateButton
					onClick={() => setDisplayCreateLayer(true)}
				>{label_create}</CreateButton>
			)}

			{(display_create_layer && create_type === false) && (
				<FloatingLayer
					backButton={() => setDisplayCreateLayer(false)}
					_class={{ size:'small' }}
				>
					<div class="without_actions">
						<SectionTitle>{label_main}</SectionTitle>

						{(isNotEmpty(props, 'format_message')) && (
							<AlertMessage
								_class={{ other:'in_top' }}
							>
								<p>{i18n(props.format_message)}</p>
							</AlertMessage>
						)}

						<IconedSelectorItems
							items={create_types}
							onSelectItem={selectAddingItemsOption}
						/>
					</div>
				</FloatingLayer>
			)}

			{(create_type == 'bulk') && (
				<FloatingLayer
					_class={{ size:'big' }}
					backButton={() => setCreateType(false)}
				>
					<TriviaBulkCreate
						{...props}
						onCreate={() => {
							setCreateType(false);
							setDisplayCreateLayer(false);
						}}
					/>
				</FloatingLayer>
			)}

			{(create_type == 'timed_quiz') && (
				<AIGenerator
					{...props}
					close={() => {
						setCreateType(false);
						setDisplayCreateLayer(false);
					}}
				/>
			)}

			{(create_type == 'crosswords') && (
				<AICrosswordsGenerator
					{...props}
					close={() => {
						setCreateType(false);
						setDisplayCreateLayer(false);
					}}
				/>
			)}

			{(create_type == 'palorder') && (
				<AIPalorderGenerator
					{...props}
					close={() => {
						setCreateType(false);
						setDisplayCreateLayer(false);
					}}
				/>
			)}
		</>
	);
}

export default injectIntl(MultiOptionsCreator);